/* =====================================================================
 * ChatArea — 메시지 목록 + 입력창
 * ===================================================================== */

import React, { useState, useRef, useCallback, useEffect, lazy, Suspense, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { css, keyframes } from 'styled-components';
import { PanelLeft, FileUp, Sun, Code2, FileSearch, TrendingUp, Settings, Construction, MessageSquare, LayoutList, HardDrive, BookOpen } from 'lucide-react';
import { useChatStore } from '@auw/stores';
import { emitAppEvent } from '@auw/stores';
import { useConversationRuntime } from '@auw/stores';
import { useUserStore } from '@auw/stores';
import type { WorkspaceMode, FileAttachment, BrowserLiveViewPayload } from '@auw/workspace-utils';
import { WORKSPACE_MODES } from '@auw/workspace-utils';
import { useAutoScroll } from '@auw/workspace-utils';
import { ChatModeProvider, type ChatMode } from './ChatModeContext';
import { MessageBubble, type ReplyToInfo } from './bubble/MessageBubble';
import { MessageInput } from './input/MessageInput';
import { useScrollToHashMessage } from './useScrollToHashMessage';
import { getSettingsModalComponent } from '../../lib/settingsModalSlot';
import { MessageSkeleton, SkeletonColumn } from '@auw/workspace-ui';
import { Button } from '@auw/workspace-ui';
import { Tooltip } from '@auw/workspace-ui';
import { EmptyState } from '@auw/workspace-ui';
import { FilePreviewPanel } from '@auw/workspace-ui';
import { LiveViewPanel } from './LiveViewPanel';
import { WorkspaceSplitLayout } from './WorkspaceSplitLayout';
import DMPage from '../dm/DMPage';
import { AppManagePage } from '@auw/workspace-ui';
import {
  isConversationLiveViewAvailable,
  normalizeConversationLiveView,
} from '@auw/workspace-utils';

const AGENT_BRAND_SUFFIXES = [
  '.team',
  '.agent',
  '.mate',
  '.rebo',
];

/* ------------------------------------------------------------------ */
/* Styles                                                              */
/* ------------------------------------------------------------------ */

const Container = styled.div<{ $dragging: boolean }>`
  flex: 1;
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
  position: relative;
  transition: background ${({ theme }) => theme.transitions.normal};

  ${({ $dragging, theme }) =>
    $dragging &&
    css`
      background: ${theme.mode === 'dark'
        ? 'rgba(255, 255, 255, 0.03)'
        : 'rgba(0, 0, 0, 0.02)'};
    `}
`;

const TopBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 16px;
  min-height: 48px;
  flex-shrink: 0;
`;

const TopBarLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const TopBarRight = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;


const MessagesContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  overscroll-behavior-y: contain;
  -webkit-overflow-scrolling: touch;
  position: relative;

  /* 스크롤바 숨김 (스크롤 기능은 유지) */
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE/Edge */
  &::-webkit-scrollbar {
    display: none; /* Chrome/Safari/Opera */
  }
`;

const MessagesInner = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100%;
`;

/* ---- Suggestion Carousel ---- */

const suggestionEnter = keyframes`
  0%   { opacity: 0; transform: translateY(16px) scale(0.97); }
  100% { opacity: 1; transform: translateY(0) scale(1); }
`;

const EmptyStateActions = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
`;

const SuggestionCarousel = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  margin-top: 0px;
  min-height: 80px;
`;

const SuggestionCard = styled.button`
  position: relative;
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 16px 24px;
  border-radius: ${({ theme }) => theme.sizes.borderRadiusLg};
  font-size: 0.9375rem;
  color: ${({ theme }) => theme.colors.textPrimary};
  line-height: 1.5;
  cursor: pointer;
  animation: ${suggestionEnter} 0.45s cubic-bezier(0.22, 1, 0.36, 1);
  transition: all 0.3s ease;
  max-width: 420px;
  width: 100%;
  text-align: left;
  
  /* C: Glassmorphism */
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(28, 28, 30, 0.46)' : 'rgba(255, 255, 255, 0.58)'};
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  border: 1px solid ${({ theme }) => theme.colors.border};
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);

  /* E: Gradient Border Glow (Pseudo-element) */
  &::before {
    content: '';
    position: absolute;
    inset: -1px;
    border-radius: inherit;
    padding: 1px;
    background: linear-gradient(135deg, #FF6B00, #FFA800);
    -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
    opacity: 0;
    transition: opacity 0.3s ease;
    pointer-events: none;
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(255, 107, 0, 0.15);
    border-color: transparent;
  }

  &:hover::before {
    opacity: 1;
  }

  &:active { transform: scale(0.98); }
`;

const SuggestionIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: ${({ theme }) =>
    theme.mode === 'dark'
      ? 'rgba(255, 255, 255, 0.06)'
      : 'rgba(0, 0, 0, 0.04)'};
  color: ${({ theme }) => theme.colors.textSecondary};
  flex-shrink: 0;
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);

  /* F: Micro Interaction */
  ${SuggestionCard}:hover & {
    transform: scale(1.15) rotate(-8deg);
    color: #FF8A00;
    background: ${({ theme }) => theme.mode === 'dark' ? 'rgba(255, 138, 0, 0.15)' : 'rgba(255, 138, 0, 0.1)'};
  }
`;

const SuggestionText = styled.span`
  flex: 1;
`;

const SuggestionBadge = styled.span`
  margin-left: auto;
  font-size: 0.7rem;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 20px;
  background: ${({ theme }) => theme.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.04)'};
  color: ${({ theme }) => theme.colors.textSecondary};
  transition: all 0.3s ease;
  white-space: nowrap;

  ${SuggestionCard}:hover & {
    background: linear-gradient(135deg, #FF6B00, #FFA800);
    color: white;
    box-shadow: 0 2px 8px rgba(255, 107, 0, 0.3);
  }
`;

const progressFill = keyframes`
  from { transform: scaleX(0); }
  to   { transform: scaleX(1); }
`;

const ProgressBars = styled.div`
  display: flex;
  gap: 4px;
  width: 160px;
`;

const ProgressBarTrack = styled.div`
  flex: 1;
  height: 3px;
  border-radius: 1.5px;
  background: ${({ theme }) => theme.colors.border};
  overflow: hidden;
  cursor: pointer;

  &:hover {
    opacity: 0.8;
  }
`;

const ProgressBarFill = styled.div<{ $state: 'done' | 'active' | 'upcoming' }>`
  height: 100%;
  border-radius: 1.5px;
  background: ${({ theme }) => theme.colors.textSecondary};
  transform-origin: left;
  transform: scaleX(0);

  ${({ $state }) =>
    $state === 'done' &&
    css`
      transform: scaleX(1);
    `}

  ${({ $state }) =>
    $state === 'active' &&
    css`
      animation: ${progressFill} ${SUGGESTION_INTERVAL}ms linear forwards;
    `}
`;

/* ---- File preview split layout ---- */

const agentPanelFadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const AGENT_NARROW_CONTENT_WIDTH = 1280;

const BodyRow = styled.div`
  flex: 1;
  display: flex;
  overflow: hidden;
  min-height: 0;
`;

const SidePanelSlot = styled.div<{ $isNarrow: boolean; $narrowHidden: boolean; $fullscreen: boolean }>`
  display: ${({ $narrowHidden }) => ($narrowHidden ? 'none' : 'flex')};
  flex-direction: column;
  overflow: hidden;
  min-width: 0;
  animation: ${agentPanelFadeIn} 0.25s ease;

  ${({ $fullscreen }) =>
    $fullscreen &&
    css`
      flex: 1;
      padding: 10px;
    `}

  ${({ $isNarrow, $fullscreen }) =>
    !$fullscreen &&
    ($isNarrow
      ? css`
          flex: 1;
          padding: 10px;
        `
      : css`
          flex: 6;
          padding: 10px 0 16px 10px;
        `)}
`;

const ChatColumn = styled.div<{ $hidden?: boolean }>`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: relative;
  min-width: 0;

  ${({ $hidden }) =>
    $hidden &&
    css`
      display: none;
    `}
`;

const InputAreaWrapper = styled.div`
  position: relative;
  flex-shrink: 0;
`;

const TeamDeletedNotice = styled.div`
  margin: 8px 16px 4px;
  padding: 10px 12px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.sizes.borderRadiusSm};
  background: ${({ theme }) => theme.colors.bgSecondary};
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: 0.875rem;
  text-align: center;
`;

const ScrollToBottomBtn = styled.button<{ $visible: boolean }>`
  position: absolute;
  bottom: calc(100% + 8px);
  left: 50%;
  transform: translateX(-50%);
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.bgSecondary};
  border: 1px solid ${({ theme }) => theme.colors.border};
  box-shadow: ${({ theme }) => theme.shadows.md};
  color: ${({ theme }) => theme.colors.textSecondary};
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  pointer-events: ${({ $visible }) => ($visible ? 'auto' : 'none')};
  transition: all ${({ theme }) => theme.transitions.fast};
  z-index: 5;

  &:hover {
    background: ${({ theme }) => theme.colors.bgHover};
    color: ${({ theme }) => theme.colors.textPrimary};
  }
`;

/* ------------------------------------------------------------------ */
/* Drop overlay                                                        */
/* ------------------------------------------------------------------ */

const overlayFadeIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

const iconFloat = keyframes`
  0%, 100% { transform: translateY(0); }
  50%      { transform: translateY(-6px); }
`;

const DropOverlayFull = styled.div`
  position: absolute;
  inset: 0;
  z-index: 50;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  animation: ${overlayFadeIn} 0.2s ease;
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  background: ${({ theme }) =>
    theme.mode === 'dark'
      ? 'rgba(23, 23, 23, 0.82)'
      : 'rgba(255, 255, 255, 0.82)'};
  pointer-events: none;
`;

const DropIconCircle = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) =>
    theme.mode === 'dark'
      ? 'rgba(255, 255, 255, 0.07)'
      : 'rgba(0, 0, 0, 0.04)'};
  animation: ${iconFloat} 2s ease-in-out infinite;
`;

const DropLabel = styled.span`
  font-size: 1rem;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.textSecondary};
  letter-spacing: -0.01em;
`;

/* ---- Disclaimer footer ---- */

const DisclaimerFooter = styled.div`
  text-align: center;
  padding: 4px 16px 8px;
  font-size: 0.75rem;
  color: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.94)' : 'rgba(0,0,0,0.88)'};
  flex-shrink: 0;
  letter-spacing: -0.01em;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 0.6875rem;
    padding: 3px 12px 6px;
  }
`;

/* ---- Placeholder (미구현 페이지) ---- */

const PlaceholderWrap = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  color: ${({ theme }) => theme.colors.textMuted};
`;

const PlaceholderIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
  border-radius: 16px;
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)'};
`;

const PlaceholderLabel = styled.span`
  font-size: 1rem;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const PlaceholderSub = styled.span`
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.colors.textMuted};
`;

/** 미구현 페이지 placeholder 컴포넌트 */
const PlaceholderPage: React.FC<{ mode: WorkspaceMode }> = ({ mode }) => {
  const { t } = useTranslation('common');
  const info = WORKSPACE_MODES[mode];
  const ModeIcon = info?.icon ?? Construction;
  return (
    <PlaceholderWrap>
      <PlaceholderIcon>
        <ModeIcon size={32} />
      </PlaceholderIcon>
      <PlaceholderLabel>{info ? t(info.labelKey) : mode}</PlaceholderLabel>
      <PlaceholderSub>{t('chat.preparing')}</PlaceholderSub>
    </PlaceholderWrap>
  );
};

/* ------------------------------------------------------------------ */
/* Suggestions                                                         */
/* ------------------------------------------------------------------ */

const suggestions = [
  { textKey: 'chat.examplePrompt1', icon: Sun, badgeKey: 'chat.exampleBadge1' },
  { textKey: 'chat.examplePrompt2', icon: Code2, badgeKey: 'chat.exampleBadge2' },
  { textKey: 'chat.examplePrompt3', icon: FileSearch, badgeKey: 'chat.exampleBadge3' },
  { textKey: 'chat.examplePrompt4', icon: TrendingUp, badgeKey: 'chat.exampleBadge4' },
  { textKey: 'chat.examplePrompt5', icon: MessageSquare, badgeKey: 'chat.exampleBadge5' },
  { textKey: 'chat.examplePrompt6', icon: LayoutList, badgeKey: 'chat.exampleBadge6' },
  { textKey: 'chat.examplePrompt7', icon: HardDrive, badgeKey: 'chat.exampleBadge7' },
  { textKey: 'chat.examplePrompt8', icon: BookOpen, badgeKey: 'chat.exampleBadge8' },
];

const SUGGESTION_INTERVAL = 3500; // ms

/* ------------------------------------------------------------------ */
/* Component                                                           */
/* ------------------------------------------------------------------ */

/** Optional slot props let feature shells (e.g. doc-agent) inject UI
 * around the standard ChatArea regions without forking the component.
 * All slots default to ``null`` — passing none keeps the unified-agent
 * layout byte-identical to pre-PR-FE-1. */
export interface ChatAreaProps {
  /** Workspace mode the chat tree is mounted under. Drives feature
   * flag namespacing (``mode.<mode>.<feature>``) inside descendants
   * via :func:`useChatFeature`. Defaults to ``'agent'``. */
  mode?: ChatMode;
  /** Rendered above the messages list, inside the scrolling container. */
  aboveMessagesSlot?: React.ReactNode;
  /** Rendered between the messages list and the input bar — natural
   * home for review/confirm panels (e.g. doc-agent's PatchReviewPanel). */
  belowMessagesSlot?: React.ReactNode;
  /** Rendered just above the MessageInput composer — natural home for
   * context chips (e.g. doc-agent's selection chip). */
  aboveInputSlot?: React.ReactNode;
}

export const ChatArea: React.FC<ChatAreaProps> = ({
  mode = 'agent',
  aboveMessagesSlot,
  belowMessagesSlot,
  aboveInputSlot,
}) => {
  const { t } = useTranslation('common');
  const messages = useChatStore((s) => s.activeMessages());
  const sidebarOpen = useChatStore((s) => s.sidebarOpen);
  const setSidebarOpen = useChatStore((s) => s.setSidebarOpen);
  const sendMessage = useChatStore((s) => s.sendMessage);
  const activeConv = useChatStore((s) => s.activeConversation());
  const conversationId = activeConv?.id ?? null;
  const { runtime, isStreaming: streaming } = useConversationRuntime(conversationId);
  const previewFile = useChatStore((s) => s.previewFile);
  const setPreviewFile = useChatStore((s) => s.setPreviewFile);
  const previewFullscreen = useChatStore((s) => s.previewFullscreen);
  const togglePreviewFullscreen = useChatStore((s) => s.togglePreviewFullscreen);
  const activeMode = useChatStore((s) => s.activeMode);
  const toggleReaction = useChatStore((s) => s.toggleReaction);
  const regenerateMessage = useChatStore((s) => s.regenerateMessage);
  const applyConversationLiveViewEvent = useChatStore((s) => s.applyConversationLiveViewEvent);
  const setConversationLiveViewOpen = useChatStore((s) => s.setConversationLiveViewOpen);
  const narrowPreviewView = useChatStore((s) => s.narrowPreviewView);
  const setNarrowLayout = useChatStore((s) => s.setNarrowLayout);
  const currentUserId = useUserStore((s) => s.user?.userId || s.user?.email || '');
  const currentUserName = useUserStore((s) => s.user?.name || s.user?.email || '');
  const currentUserPicture = useUserStore((s) => s.user?.picture ?? null);
  const sharedGroup = activeConv?.sharedGroup ?? null;
  const isTeamDeleted = sharedGroup?.state === 'team_deleted';
  const resolvedConversationId = conversationId ?? '';
  const liveViewRuntime = runtime?.liveView ?? null;
  const lastMessage = messages[messages.length - 1] ?? null;
  const autoScrollDeps = [
    lastMessage?.id ?? 'empty',
    lastMessage?.content.length ?? 0,
    lastMessage?.streamEvents?.length ?? 0,
  ];

  const [settingsModalOpen, setSettingsModalOpen] = useState(false);
  const { containerRef, scrollToBottom, isNearBottom, handleScroll } = useAutoScroll(
    autoScrollDeps,
    streaming,
    resolvedConversationId,
  );

  const isEmpty = messages.length === 0;
  /* 파일 저장소의 "출처" 바로가기로 들어온 #m-{messageId} 해시를 처리해
   * 메시지가 DOM 에 마운트되는 즉시 해당 버블로 부드럽게 스크롤한다. */
  useScrollToHashMessage(containerRef, !isEmpty);
  const liveViewWorkspaceType = activeConv?.mode ?? activeMode;
  const effectiveLiveView = useMemo(() => {
    const persisted = activeConv?.liveView ?? null;
    const hasRuntime = !!liveViewRuntime?.liveViewRef;
    const hasPersisted = !!persisted?.liveViewRef;
    if (!hasRuntime && !hasPersisted) return null;
    return {
      liveViewRef: liveViewRuntime?.liveViewRef ?? persisted?.liveViewRef ?? null,
      available: liveViewRuntime?.available ?? persisted?.available ?? false,
      watchOnly: liveViewRuntime?.watchOnly ?? persisted?.watchOnly ?? true,
      state: liveViewRuntime?.state ?? persisted?.state ?? null,
      errorMessage: liveViewRuntime?.errorMessage ?? persisted?.errorMessage ?? null,
      open: liveViewRuntime?.open ?? false,
      running: liveViewRuntime?.running ?? false,
    };
  }, [activeConv?.liveView, liveViewRuntime]);
  const liveViewAvailable = useMemo(() => isConversationLiveViewAvailable(normalizeConversationLiveView({
    liveViewRef: effectiveLiveView?.liveViewRef ?? null,
    state: effectiveLiveView?.state ?? 'closed',
    available: effectiveLiveView?.available ?? false,
    watchOnly: effectiveLiveView?.watchOnly ?? true,
    errorMessage: effectiveLiveView?.errorMessage ?? null,
  })), [effectiveLiveView]);
  const showLiveViewPanel = !!(
    !previewFile
    && conversationId
    && effectiveLiveView?.open
    && effectiveLiveView.liveViewRef
    && liveViewAvailable
  );
  const hasSidePanel = !!previewFile || showLiveViewPanel;
  const isSidePanelFullscreen = !!previewFile && previewFullscreen;
  const bodyRowRef = useRef<HTMLDivElement>(null);
  const [isAgentNarrow, setIsAgentNarrow] = useState(false);

  useEffect(() => {
    if (activeMode !== 'agent') {
      setIsAgentNarrow(false);
      return;
    }
    const element = bodyRowRef.current;
    if (!element) return;
    const observer = new ResizeObserver(([entry]) => {
      const narrow = entry.contentRect.width < AGENT_NARROW_CONTENT_WIDTH;
      setIsAgentNarrow(narrow);
      setNarrowLayout(narrow);
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, [activeMode, setNarrowLayout]);

  const isNarrowSidePanelHidden = isAgentNarrow && hasSidePanel && narrowPreviewView === 'chat';
  const isNarrowChatHidden = isAgentNarrow && hasSidePanel && narrowPreviewView === 'preview';

  const handleCloseLiveView = useCallback(() => {
    if (!conversationId) return;
    setConversationLiveViewOpen(conversationId, false);
  }, [conversationId, setConversationLiveViewOpen]);

  const handleLiveViewSessionClosed = useCallback((errorMessage?: string | null) => {
    if (!conversationId || !effectiveLiveView?.liveViewRef) return;
    const closedPayload: BrowserLiveViewPayload = {
      kind: 'browser_live_view',
      state: 'closed',
      live_view_ref: effectiveLiveView.liveViewRef,
      available: false,
      watch_only: effectiveLiveView.watchOnly,
      item_id: conversationId,
      workspace_type: liveViewWorkspaceType,
      updated_at: new Date().toISOString(),
      ...(errorMessage ? { error_message: errorMessage } : {}),
    };
    applyConversationLiveViewEvent(conversationId, closedPayload, {
      autoOpen: false,
      running: false,
    });
  }, [
    applyConversationLiveViewEvent,
    conversationId,
    effectiveLiveView?.liveViewRef,
    effectiveLiveView?.watchOnly,
    liveViewWorkspaceType,
  ]);

  /* ---- Suggestion carousel ---- */
  const [activeIdx, setActiveIdx] = useState(0);

  useEffect(() => {
    if (!isEmpty) return;
    const timer = setInterval(() => {
      setActiveIdx((prev) => (prev + 1) % suggestions.length);
    }, SUGGESTION_INTERVAL);
    return () => clearInterval(timer);
  }, [isEmpty]);

  /* ---- Drag & Drop ---- */
  const [dragging, setDragging] = useState(false);
  const dragCounter = useRef(0);

  const hasFiles = (e: React.DragEvent) =>
    e.dataTransfer.types.includes('Files');

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    if (!hasFiles(e)) return;               // 내부 드래그(시트 탭 등)는 무시
    dragCounter.current += 1;
    if (dragCounter.current === 1) setDragging(true);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    if (!hasFiles(e)) return;
    dragCounter.current -= 1;
    if (dragCounter.current <= 0) {
      dragCounter.current = 0;
      setDragging(false);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    dragCounter.current = 0;
    setDragging(false);
    const files = Array.from(e.dataTransfer.files ?? []);
    if (files.length === 0) return;
    window.dispatchEvent(new CustomEvent('chat:addFiles', { detail: { files } }));
  }, []);

  return (
    <ChatModeProvider value={mode}>
      <Container
        $dragging={dragging}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
      {/* Top bar — DM 모드에서는 숨김 */}
      {activeMode !== 'message' && activeMode !== 'app-manage' && (
        <TopBar>
          <TopBarLeft>
            {!sidebarOpen && (
              <Tooltip label={t('tooltip.sidebar')} position="bottom" align="start">
                <Button variant="ghost" size="icon" onClick={() => setSidebarOpen(true)}>
                  <PanelLeft size={18} />
                </Button>
              </Tooltip>
            )}
            
          </TopBarLeft>
          <TopBarRight>
            <Tooltip label={t('sidebar.settings')} position="bottom">
              <Button variant="ghost" size="icon" onClick={() => setSettingsModalOpen(true)}>
                <Settings size={18} />
              </Button>
            </Tooltip>
          </TopBarRight>
        </TopBar>
      )}

      {/* ---- Mode content router ---- */}
      {activeMode === 'agent' ? (
        /* Agent (기존 채팅) — 자체 레이아웃 */
        <BodyRow ref={bodyRowRef}>
          {hasSidePanel && (
            <SidePanelSlot
              className={previewFile ? 'preview-embedded' : undefined}
              $isNarrow={isAgentNarrow}
              $narrowHidden={isNarrowSidePanelHidden}
              $fullscreen={isSidePanelFullscreen}
            >
              {previewFile ? (
                <FilePreviewPanel
                  file={previewFile}
                  onClose={() => setPreviewFile(null)}
                  fullscreen={previewFullscreen}
                  onToggleFullscreen={togglePreviewFullscreen}
                />
              ) : (
                showLiveViewPanel
                && conversationId
                && effectiveLiveView?.liveViewRef && (
                  <LiveViewPanel
                    key={effectiveLiveView.liveViewRef}
                    liveViewRef={effectiveLiveView.liveViewRef}
                    conversationId={conversationId}
                    workspaceType={liveViewWorkspaceType}
                    watchOnly={effectiveLiveView.watchOnly !== false}
                    onClose={handleCloseLiveView}
                    onSessionClosed={handleLiveViewSessionClosed}
                  />
                )
              )}
            </SidePanelSlot>
          )}
          <ChatColumn $hidden={isSidePanelFullscreen || isNarrowChatHidden}>
            {isEmpty && !hasSidePanel ? (
              <EmptyState
                titleSize="1.75rem"
                titlePrefix="Askyour"
                rotatingTitles={AGENT_BRAND_SUFFIXES}
                description={t('chat.welcomeMessage')}
                descriptionTone="strong"
                action={
                  <EmptyStateActions>
                    <SuggestionCarousel>
                  <SuggestionCard
                    key={activeIdx}
                    onClick={() => sendMessage(t(suggestions[activeIdx].textKey))}
                  >
                    <SuggestionIcon>
                      {React.createElement(suggestions[activeIdx].icon, { size: 18 })}
                    </SuggestionIcon>
                    <SuggestionText>{t(suggestions[activeIdx].textKey)}</SuggestionText>
                    <SuggestionBadge>{t(suggestions[activeIdx].badgeKey)}</SuggestionBadge>
                  </SuggestionCard>
                      <ProgressBars>
                        {suggestions.map((_, i) => {
                          const state = i < activeIdx ? 'done' : i === activeIdx ? 'active' : 'upcoming';
                          return (
                            <ProgressBarTrack key={i} onClick={() => setActiveIdx(i)}>
                              <ProgressBarFill key={`fill-${i}-${activeIdx}`} $state={state} />
                            </ProgressBarTrack>
                          );
                        })}
                      </ProgressBars>
                    </SuggestionCarousel>
                  </EmptyStateActions>
                }
              />
            ) : (
              <MessagesContainer ref={containerRef} onScroll={handleScroll}>
                <MessagesInner>
                  {aboveMessagesSlot}
                  {messages.map((msg, idx) => {
                    let replyTo: ReplyToInfo | null = null;
                    if (sharedGroup && msg.role === 'assistant') {
                      for (let i = idx - 1; i >= 0; i--) {
                        const prev = messages[i];
                        if (prev.role === 'user') {
                          const selfDisplayName = currentUserName.trim() || t('chat.me');
                          const isMe = !!prev.senderId && prev.senderId === currentUserId;
                          const member = prev.senderId ? sharedGroup.members.find((m) => m.user_id === prev.senderId) : null;
                          const isImmediatePrev = i === idx - 1;
                          const likelyMineWithoutSender =
                            isImmediatePrev
                            && !prev.senderId
                            && !prev.senderName;
                          const resolvedSenderName = isMe
                            ? selfDisplayName
                            : (prev.senderName
                                || member?.name
                                || member?.email
                                || (likelyMineWithoutSender ? selfDisplayName : t('chat.teamMember')));
                          replyTo = {
                            messageId: prev.id,
                            senderName: resolvedSenderName,
                            senderPicture: isMe
                              ? currentUserPicture
                              : (member?.picture ?? (likelyMineWithoutSender ? currentUserPicture : null)),
                            content: prev.content,
                          };
                          break;
                        }
                      }
                    }
                    return (
                      <MessageBubble
                        key={msg.id}
                        narrowColumn={hasSidePanel}
                        message={msg}
                        sharedGroup={sharedGroup}
                        currentUserId={currentUserId}
                        conversationId={resolvedConversationId}
                        onToggleReaction={toggleReaction}
                        replyTo={replyTo}
                        onScrollToMessage={(messageId) => {
                          const el = containerRef.current?.querySelector(`[data-message-id="${messageId}"]`);
                          el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        }}
                        onRegenerate={
                          msg.role === 'assistant' && !msg.replacedByMessageId && resolvedConversationId
                            ? (messageId, forceMode) => regenerateMessage(resolvedConversationId, messageId, forceMode)
                            : undefined
                        }
                      />
                    );
                  })}
                  {streaming && messages.length > 0 && messages[messages.length - 1].role === 'user' && (
                    <SkeletonColumn>
                      <MessageSkeleton />
                    </SkeletonColumn>
                  )}
                </MessagesInner>
              </MessagesContainer>
            )}
          </ChatColumn>
        </BodyRow>
      ) : activeMode === 'message' ? (
        /* DM mode — 전용 메신저 레이아웃 */
        <DMPage />
      ) : activeMode === 'app-manage' ? (
        /* 앱 관리 — owner 전용 */
        <AppManagePage />
      ) : (
        /* 기타 모드 — 좌: 플레이스홀더, 우: AI 채팅 */
        <WorkspaceSplitLayout primaryContent={<PlaceholderPage mode={activeMode} />} />
      )}

      {/* ---- 공통 하단: belowMessagesSlot + MessageInput + Disclaimer
           (DM 모드 제외) ---- */}
      {activeMode !== 'message' && activeMode !== 'app-manage' && (
        <>
          {belowMessagesSlot}
          <InputAreaWrapper>
            <ScrollToBottomBtn
              $visible={activeMode === 'agent' && !isEmpty && !isNearBottom && !streaming}
              onClick={() => scrollToBottom()}
            >
              ↓
            </ScrollToBottomBtn>
            {isTeamDeleted ? (
              <TeamDeletedNotice>{t('error.teamSpaceDeleted')}</TeamDeletedNotice>
            ) : (
              <>
                {aboveInputSlot}
                <MessageInput />
              </>
            )}
          </InputAreaWrapper>
          <DisclaimerFooter>
            {t('chat.aiDisclaimer')}
          </DisclaimerFooter>
        </>
      )}

      {/* Drop overlay */}
      {dragging && (
        <DropOverlayFull>
          <DropIconCircle>
            <FileUp size={36} strokeWidth={1.5} color="currentColor" />
          </DropIconCircle>
          <DropLabel>{t('chat.dropFilesHere')}</DropLabel>
        </DropOverlayFull>
      )}

      {/* Modals */}
      <Suspense fallback={null}>
        {(() => {
          const SettingsModalImpl = getSettingsModalComponent();
          return SettingsModalImpl
            ? <SettingsModalImpl open={settingsModalOpen} onClose={() => setSettingsModalOpen(false)} />
            : null;
        })()}
      </Suspense>
      </Container>
    </ChatModeProvider>
  );
};

